/**
 * StatsCard - displays a single dashboard statistic
 */

interface StatsCardProps {
  title: string;
  value: number | string;
  change?: number | undefined;
  changeLabel?: string | undefined;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'gray';
}

const colorClasses = {
  blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  green: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
  yellow: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
  red: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  purple: 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
  gray: 'bg-neutral-50 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400',
};

export function StatsCard({
  title,
  value,
  change,
  changeLabel,
  icon,
  color,
}: StatsCardProps) {
  const isPositive = change !== undefined && change >= 0;

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</p>
          <div className="flex items-baseline">
            <p className="text-2xl font-semibold text-neutral-900 dark:text-white">{value}</p>
            {change !== undefined && (
              <span
                className={`ml-2 text-sm font-medium ${
                  isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}
              >
                {isPositive ? '+' : ''}
                {change}%
                {changeLabel && (
                  <span className="text-neutral-500 dark:text-neutral-400 ml-1">{changeLabel}</span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
